import { X } from "lucide-react";
import React, { useState } from "react";
import ButtonV2 from "../../../../components/ui/button/v2";
import SelectDropdownV2 from "../../../../components/ui/selectdropdown/v2";
import { OptionNameValuePair } from "../../../../lib/interfaces/types";
import { cn } from "../../../../lib/utils";

interface IAddNewPartyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (partyName: string, partyType: string) => void;
}

enum PartyType {
  VENDOR = "VENDOR",
  OTHER = "OTHER",
}

const PARTY_TYPE_OPTIONS: OptionNameValuePair<string>[] = [
  { name: "Vendor", value: PartyType.VENDOR },
  { name: "Other", value: PartyType.OTHER },
];

const AddNewPartyModal: React.FC<IAddNewPartyModalProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [partyName, setPartyName] = useState("");
  const [partyType, setPartyType] = useState<string>("");

  const handleSave = () => {
    if (partyName.trim() && partyType) {
      onSave(partyName, partyType);
      handleClose();
    }
  };

  const handleClose = () => {
    setPartyName("");
    setPartyType("");
    onClose();
  };

  if (!isOpen) return null;

  const isSaveDisabled = !partyName.trim() || !partyType;

  const renderBackdrop = () => (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-40"
      onClick={handleClose}
    />
  );

  const renderHeader = () => (
    <div className="flex flex-row justify-between items-start">
      <span className="font-sans font-semibold text-lg leading-[100%] tracking-[0%] text-[#0A0A0A]">
        Add new party details
      </span>
      <button
        onClick={handleClose}
        className="p-1 hover:bg-gray-100 rounded-md transition-colors"
        aria-label="Close modal"
      >
        <X className="w-4 h-4 text-[#0A0A0A]" strokeWidth={1.33} />
      </button>
    </div>
  );

  const renderPartyNameField = () => (
    <div className="flex flex-col gap-2">
      <label className="font-sans font-medium text-sm leading-[100%] tracking-[0%] text-[#0A0A0A]">
        Party Name
      </label>
      <input
        type="text"
        value={partyName}
        onChange={(e) => setPartyName(e.target.value)}
        placeholder="Enter party name"
        className={cn(
          "w-[377px] h-9 rounded-md border border-gray-300 px-3 py-1",
          "font-sans font-normal text-sm text-[#0A0A0A]",
          "placeholder:text-gray-400",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        )}
      />
    </div>
  );

  const renderPartyTypeField = () => (
    <div className="flex flex-col gap-2">
      <label className="font-sans font-medium text-sm leading-[100%] tracking-[0%] text-[#0A0A0A]">
        Party Type
      </label>
      <div className="w-[377px] h-9">
        <SelectDropdownV2
          name="Party Type"
          options={PARTY_TYPE_OPTIONS}
          selectedOption={PARTY_TYPE_OPTIONS.find(
            (opt) => opt.value === partyType
          )}
          onSelectionChange={(selected) => setPartyType(selected.value)}
          containerClassName="w-full h-full"
          labelClassName={cn(
            "h-full border border-gray-300 rounded-md px-3 py-1",
            "font-sans font-normal text-sm text-[#0A0A0A]"
          )}
          placeholder="Select party type"
          enableSearch={false}
        />
      </div>
    </div>
  );

  const renderCancelButton = () => (
    <ButtonV2
      variant="outline"
      onClick={handleClose}
      className="font-sans font-medium text-sm leading-5 w-[182.5px] h-9 border-[#3551F3] text-[#3551F3]"
    >
      Discard
    </ButtonV2>
  );

  const renderAddButton = () => (
    <ButtonV2
      variant="primary"
      onClick={handleSave}
      disabled={isSaveDisabled}
      className={cn(
        "font-sans font-medium text-sm leading-5 w-[182.5px] h-9",
        isSaveDisabled && "opacity-50 cursor-not-allowed"
      )}
    >
      Save Changes
    </ButtonV2>
  );

  const renderFooter = () => (
    <div className="flex flex-row justify-around items-center gap-2 mt-1">
      {renderCancelButton()}
      {renderAddButton()}
    </div>
  );

  const renderModalContent = () => (
    <div className="flex flex-col gap-4">
      {renderHeader()}
      {renderPartyNameField()}
      {renderPartyTypeField()}
      {renderFooter()}
    </div>
  );

  const renderModal = () => (
    <div
      className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-lg shadow-lg w-[425px] h-[280px] max-w-[425px] p-6 gap-4 border-[2px] border-[#E5E5E5] "
      onClick={(e) => e.stopPropagation()}
    >
      {renderModalContent()}
    </div>
  );

  return (
    <>
      {renderBackdrop()}
      {renderModal()}
    </>
  );
};

export default AddNewPartyModal;
